import type { FilterModel } from "~/components/grid/AgGrid";
import type { FilterType } from "~/helpers/ag-grid/createFilteredColumn";

export type GraphQLFilter = Record<string, unknown>;

interface AgFilterCondition {
	dateFrom?: string | null;
	dateTo?: string | null;
	filter?: number | string | null;
	filterTo?: number | string | null;
	type?: string;
}

interface AgFilterDef extends AgFilterCondition {
	conditions?: AgFilterCondition[];
	operator?: string;
	values?: (boolean | string | null)[];
}

interface AgMultiFilterDef {
	filterModels: (AgFilterDef | null)[];
	filterType: "multi";
}

export type SpecialFieldHandler = (filterDef: AgFilterDef) => GraphQLFilter[];

const isNumericValue = (value: unknown): boolean =>
	typeof value === "number" || (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value)));

const detectFilterType = (filterDef: AgFilterDef): Exclude<FilterType, "bool"> | "complex" => {
	if (filterDef.values !== undefined) return "set";
	if (filterDef.dateFrom !== undefined || filterDef.dateTo !== undefined) return "date";
	if (filterDef.operator && filterDef.conditions) return "complex";
	if (isNumericValue(filterDef.filter)) return "number";
	return "text";
};

const convertTextCondition = (cond: AgFilterCondition, field: string): GraphQLFilter | null => {
	const { filter, type } = cond;
	if (filter == null && type !== "blank" && type !== "notBlank") return null;
	switch (type) {
		case "equals":
			return { [field]: { eq: filter } };
		case "notEqual":
			return { [field]: { neq: filter } };
		case "contains":
			return { [field]: { contains: filter } };
		case "notContains":
			return { [field]: { ncontains: filter } };
		case "startsWith":
			return { [field]: { startsWith: filter } };
		case "endsWith":
			return { [field]: { endsWith: filter } };
		case "blank":
			return { [field]: { eq: null } };
		case "notBlank":
			return { [field]: { neq: null } };
		default:
			return { [field]: { contains: filter } };
	}
};

const convertNumberCondition = (cond: AgFilterCondition, field: string): GraphQLFilter | null => {
	const { filter, filterTo, type } = cond;
	if (filter == null && type !== "blank" && type !== "notBlank") return null;
	switch (type) {
		case "equals":
			return { [field]: { eq: Number(filter) } };
		case "notEqual":
			return { [field]: { neq: Number(filter) } };
		case "greaterThan":
			return { [field]: { gt: Number(filter) } };
		case "greaterThanOrEqual":
			return { [field]: { gte: Number(filter) } };
		case "lessThan":
			return { [field]: { lt: Number(filter) } };
		case "lessThanOrEqual":
			return { [field]: { lte: Number(filter) } };
		case "inRange":
			return { and: [{ [field]: { gte: Number(filter) } }, { [field]: { lte: Number(filterTo) } }] };
		case "blank":
			return { [field]: { eq: null } };
		case "notBlank":
			return { [field]: { neq: null } };
		default:
			return null;
	}
};

const convertCombinable = (
	filterDef: AgFilterDef,
	field: string,
	convertCondition: (cond: AgFilterCondition, field: string) => GraphQLFilter | null
): GraphQLFilter[] => {
	if (filterDef.operator && filterDef.conditions) {
		const subConditions = filterDef.conditions
			.map((cond) => convertCondition(cond, field))
			.filter((condition): condition is GraphQLFilter => condition !== null);
		return subConditions.length > 0 ? [{ [filterDef.operator.toLowerCase()]: subConditions }] : [];
	}
	const condition = convertCondition(filterDef, field);
	return condition ? [condition] : [];
};

const fullDayRange = (value: string): { end: string; start: string } => {
	const start = new Date(value);
	start.setHours(0, 0, 0, 0);
	const end = new Date(value);
	end.setHours(23, 59, 59, 999);
	return { end: end.toISOString(), start: start.toISOString() };
};

const convertDateFilter = (filterDef: AgFilterDef, field: string): GraphQLFilter[] => {
	const { dateFrom, dateTo, type } = filterDef;
	if (!dateFrom && type !== "blank" && type !== "notBlank") return [];
	switch (type) {
		case "equals": {
			const { end, start } = fullDayRange(dateFrom as string);
			return [{ and: [{ [field]: { gte: start } }, { [field]: { lte: end } }] }];
		}
		case "notEqual": {
			const { end, start } = fullDayRange(dateFrom as string);
			return [{ or: [{ [field]: { lt: start } }, { [field]: { gt: end } }] }];
		}
		case "greaterThan":
			return [{ [field]: { gt: new Date(dateFrom as string).toISOString() } }];
		case "lessThan":
			return [{ [field]: { lt: new Date(dateFrom as string).toISOString() } }];
		case "inRange":
			return dateTo
				? [
						{
							and: [
								{ [field]: { gte: new Date(dateFrom as string).toISOString() } },
								{ [field]: { lte: new Date(dateTo).toISOString() } }
							]
						}
					]
				: [];
		case "blank":
			return [{ [field]: { eq: null } }];
		case "notBlank":
			return [{ [field]: { neq: null } }];
		default:
			return [];
	}
};

const DATE_ONLY_PREFIX_LENGTH = 10;

const dateOnlyPrefix = (value: string): string => value.slice(0, DATE_ONLY_PREFIX_LENGTH);

const dateOnlyStartOfDay = (value: string): string => `${dateOnlyPrefix(value)}T00:00:00.000Z`;

const dateOnlyEndOfDay = (value: string): string => `${dateOnlyPrefix(value)}T23:59:59.999Z`;

const convertDateOnlyFilter = (filterDef: AgFilterDef, field: string): GraphQLFilter[] => {
	const { dateFrom, dateTo, type } = filterDef;
	if (!dateFrom && type !== "blank" && type !== "notBlank") return [];
	switch (type) {
		case "equals":
			return [
				{
					and: [
						{ [field]: { gte: dateOnlyStartOfDay(dateFrom as string) } },
						{ [field]: { lte: dateOnlyEndOfDay(dateFrom as string) } }
					]
				}
			];
		case "notEqual":
			return [
				{
					or: [
						{ [field]: { lt: dateOnlyStartOfDay(dateFrom as string) } },
						{ [field]: { gt: dateOnlyEndOfDay(dateFrom as string) } }
					]
				}
			];
		case "greaterThan":
			return [{ [field]: { gt: dateOnlyStartOfDay(dateFrom as string) } }];
		case "lessThan":
			return [{ [field]: { lt: dateOnlyStartOfDay(dateFrom as string) } }];
		case "inRange":
			return dateTo
				? [
						{
							and: [
								{ [field]: { gte: dateOnlyStartOfDay(dateFrom as string) } },
								{ [field]: { lte: dateOnlyEndOfDay(dateTo) } }
							]
						}
					]
				: [];
		case "blank":
			return [{ [field]: { eq: null } }];
		case "notBlank":
			return [{ [field]: { neq: null } }];
		default:
			return [];
	}
};

const convertSetFilter = (filterDef: AgFilterDef, field: string, coerceNumber = false): GraphQLFilter[] => {
	if (!filterDef.values) return [];
	const values = coerceNumber
		? filterDef.values.map((value) => (value == null ? null : Number(value)))
		: filterDef.values;
	return [{ [field]: { in: values } }];
};

const isMultiFilterDef = (value: unknown): value is AgMultiFilterDef =>
	typeof value === "object" && value !== null && (value as { filterType?: unknown }).filterType === "multi";

const convertMultiFilter = (filterDef: AgMultiFilterDef, field: string, primaryType: FilterType): GraphQLFilter[] => {
	const conditions: GraphQLFilter[] = [];
	for (const sub of filterDef.filterModels) {
		if (!sub) continue;
		if (sub.values !== undefined) conditions.push(...convertSetFilter(sub, field, primaryType === "number"));
		else conditions.push(...convertByType(primaryType, sub, field));
	}
	return conditions;
};

// BooleanOperationFilterInput exposes only eq/neq (no `in`); AG set filters serialize values as strings.
const convertBoolFilter = (filterDef: AgFilterDef, field: string): GraphQLFilter[] => {
	const selected = new Set(
		(filterDef.values ?? [])
			.filter((value) => value !== null && value !== undefined)
			.map((value) => String(value).toLowerCase())
	);
	const hasTrue = selected.has("true");
	const hasFalse = selected.has("false");
	if (hasTrue && hasFalse) return [];
	if (hasTrue) return [{ [field]: { eq: true } }];
	if (hasFalse) return [{ [field]: { eq: false } }];
	return [{ and: [{ [field]: { neq: true } }, { [field]: { neq: false } }] }];
};

const convertByType = (filterType: FilterType | "complex", filterDef: AgFilterDef, field: string): GraphQLFilter[] => {
	switch (filterType) {
		case "text":
			return convertCombinable(filterDef, field, convertTextCondition);
		case "number":
			return convertCombinable(filterDef, field, convertNumberCondition);
		case "date":
			return convertDateFilter(filterDef, field);
		case "dateOnly":
			return convertDateOnlyFilter(filterDef, field);
		case "set":
			return convertSetFilter(filterDef, field);
		case "bool":
			return convertBoolFilter(filterDef, field);
		case "complex": {
			if (!(filterDef.operator && filterDef.conditions?.length)) return [];
			const convertCondition = isNumericValue(filterDef.conditions[0].filter)
				? convertNumberCondition
				: convertTextCondition;
			return convertCombinable(filterDef, field, convertCondition);
		}
		default:
			return [];
	}
};

const convertAgGridFilterToGraphQL = (
	filterModel: FilterModel | null | undefined,
	fieldTypeMap?: Record<string, FilterType>,
	specialFieldHandlers?: Record<string, SpecialFieldHandler>
): GraphQLFilter | undefined => {
	if (!filterModel || Object.keys(filterModel).length === 0) return undefined;

	const andConditions: GraphQLFilter[] = [];
	for (const [field, rawDef] of Object.entries(filterModel as Record<string, AgFilterDef>)) {
		const handler = specialFieldHandlers?.[field];
		if (handler) {
			andConditions.push(...handler(rawDef));
			continue;
		}
		if (isMultiFilterDef(rawDef)) {
			const primaryType = fieldTypeMap?.[field] ?? "text";
			andConditions.push(...convertMultiFilter(rawDef, field, primaryType));
			continue;
		}
		const filterType = fieldTypeMap?.[field] ?? detectFilterType(rawDef);
		andConditions.push(...convertByType(filterType, rawDef, field));
	}

	if (andConditions.length === 0) return undefined;
	if (andConditions.length === 1) return andConditions[0];
	return { and: andConditions };
};

export default convertAgGridFilterToGraphQL;
